import { Sentry, configureSentry } from '@/api/sentry'
import Image from '@/components/Image.vue'
import Alert from '@/components/ui/Alert.vue'
import Card from '@/components/ui/Card.vue'
import Empty from '@/components/ui/Empty.vue'
import Icon from '@/components/ui/Icon.vue'
import TButton from '@/components/ui/TButton.vue'
import TCheckbox from '@/components/ui/TCheckbox.vue'
import TCheckboxGroup from '@/components/ui/TCheckboxGroup.vue'
import TInput from '@/components/ui/TInput.vue'
import TRadioGroup from '@/components/ui/TRadioGroup.vue'
import TSelect from '@/components/ui/TSelect.vue'
import TSwitch from '@/components/ui/TSwitch.vue'
import TTextarea from '@/components/ui/TTextarea.vue'
import Layout from '@/layouts/RawLayout.vue'
import VueCustomTooltip from '@adamdehaven/vue-custom-tooltip'
import { Head, Link, createInertiaApp } from '@inertiajs/vue3'
import { createApp, h } from 'vue'
import VueLazyLoad from 'vue3-lazyload'
import ui from '@nuxt/ui/vue-plugin'
import 'v-calendar/dist/style.css'
import '../../index.css'

import FilterOffIcon from '@/components/icons/FilterOffIcon.vue'
import HubspotIcon from '@/components/icons/HubspotIcon.vue'
import DriveIcon from '@/components/icons/DriveIcon.vue'
import LoadingIcon from '@/components/icons/LoadingIcon.vue'
import MaskIcon from '@/components/icons/MaskIcon.vue'
import SolidSaveIcon from '@/components/icons/SolidSaveIcon.vue'
import TrophyIcon from '@/components/icons/TrophyIcon.vue'
import * as outlineIcons from '@heroicons/vue/outline'
import * as solidIcons from '@heroicons/vue/solid'
import type { LoggedUser } from '@/types/app'

const pages = import.meta.glob('./pages/**/*.vue')

createInertiaApp({
  progress: {
    // The color of the request progress bar.
    color: '#2469FF',
  },
  resolve: (name) => {
    // [2]
    return pages[`./pages/${name}.vue`]().then((module: any) => {
      const page = module.default
      page.layout = page.layout || Layout
      return module.default
    })
  },
  title: (title) => `${title} | Archibien`,
  setup({ el, App, props, plugin }) {
    const vueApp = createApp({
      render: () => h(App, props),
    })
      .use(plugin)
      .use(ui)
      .use(VueCustomTooltip, {
        name: 'VueCustomTooltip',
        color: '#fff',
        background: '#000',
        borderRadius: 10,
        fontWeight: 400,
      })
      .use(VueLazyLoad, {})
      .component('Link', Link)
      .component('Head', Head)
      .component('Alert', Alert)
      .component('Card', Card)
      .component('TInput', TInput)
      .component('TButton', TButton)
      .component('TTextarea', TTextarea)
      .component('TRadioGroup', TRadioGroup)
      .component('TCheckbox', TCheckbox)
      .component('TCheckboxGroup', TCheckboxGroup)
      .component('TSwitch', TSwitch)
      .component('TSelect', TSelect)
      .component('Icon', Icon)
      .component('LoadingIcon', LoadingIcon)
      .component('FilterOffIcon', FilterOffIcon)
      .component('MaskIcon', MaskIcon)
      .component('HubspotIcon', HubspotIcon)
      .component('DriveIcon', DriveIcon)
      .component('TrophyIcon', TrophyIcon)
      .component('Image', Image)
      .component('Empty', Empty)

    Object.entries(outlineIcons).forEach((entry) => {
      vueApp.component(entry[0], outlineIcons[entry[0]])
    })
    Object.entries(solidIcons).forEach((entry) => {
      if (entry[0] !== 'SaveIcon') {
        vueApp.component(`Solid${entry[0]}`, solidIcons[entry[0]])
      }
    })

    // use our own icon for save
    vueApp.component('SolidSaveIcon', SolidSaveIcon)

    configureSentry(vueApp)

    // At this point props are populated by Inertia
    const user = props.initialPage.props.user as LoggedUser | undefined
    if (user) {
      Sentry.setUser({
        id: user.id,
        username: user.full_name,
        email: user.email,
      })
    }

    vueApp.mount(el)
  },
})
